// DevIdentityBadge — says which worktree, branch and origin you are actually looking at.
//
// WHY. Several worktrees run dev servers against this repo, and nothing on screen distinguished
// them. Combined with a shared origin that made three separate failures unattributable on
// 2026-09-06 (docs/REVIEW_LOG.md): an AFE quota error from four demo seeds in one localStorage
// budget, basemap regressions from another branch's service worker, and a zipper that rendered as
// a single well because the seed predated a schema change. Each was diagnosed by hand. The badge
// makes the first question — "what am I even running?" — answerable at a glance.
//
// Dev only. `import.meta.env.DEV` is statically false in a production build, so this component and
// everything it imports drop out at bundle time.

import { useCallback, useEffect, useState } from "react";
import { browserTargets, resetDevEnvironment } from "../../lib/devReset";
import { CORPUS_MODE } from "../../features/wells/store/corpusMode";

const KB = 1024;

/** UTF-16 code units, which is the unit browsers actually meter the localStorage quota in —
 *  so this is the number that matters, not the UTF-8 byte length. */
function localStorageUnits(): number {
  try {
    let n = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k === null) continue;
      n += k.length + (localStorage.getItem(k)?.length ?? 0);
    }
    return n;
  } catch {
    return 0; // private mode, or storage blocked by policy
  }
}

/** Save the backup the reset produced. A dev-only convenience, so a failure to download is
 *  reported rather than fatal — the reset itself has already happened by then. */
function download(name: string, body: string): void {
  const url = URL.createObjectURL(
    new Blob([body], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function DevIdentityBadge(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [bytes, setBytes] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBytes(localStorageUnits());
  }, [open]);

  const reset = useCallback(async () => {
    // Destructive and irreversible in the browser, so it asks — and the backup downloads
    // BEFORE anything is cleared being the point, the confirm names what it will take.
    if (
      !window.confirm(
        "Reset this origin?\n\n" +
          "Unregisters service workers, drops caches, clears local and session storage.\n" +
          "A JSON backup downloads first. The page then reloads.",
      )
    )
      return;
    setBusy(true);
    try {
      const report = await resetDevEnvironment(browserTargets());
      try {
        download(
          `oxbo-origin-backup-${Date.now()}.json`,
          JSON.stringify(report.backup, null, 2),
        );
      } catch {
        setNote("Reset done, but the backup download was blocked.");
      }
      if (report.failures.length) {
        setNote(
          `Reset with ${report.failures.length} failure(s): ${report.failures[0]}`,
        );
        setBusy(false);
        return;
      }
      window.location.reload();
    } catch (e) {
      setNote(`Reset failed: ${(e as Error).message}`);
      setBusy(false);
    }
  }, []);

  if (!import.meta.env.DEV) return null;

  const rows: Array<[string, string]> = [
    ["worktree", import.meta.env.OXBO_WORKTREE],
    ["branch", import.meta.env.OXBO_BRANCH],
    ["origin", window.location.origin],
    ["corpus", CORPUS_MODE ? "on (read-only DB)" : "off (local demo seed)"],
    ["localStorage", `${(bytes / KB).toFixed(0)} KB`],
  ];

  return (
    <div
      style={{
        position: "fixed",
        left: 8,
        bottom: 8,
        zIndex: 99999,
        font: "11px ui-monospace, SFMono-Regular, Menlo, monospace",
        background: "rgba(17,17,17,.88)",
        color: "#e6e6e6",
        border: "1px solid #444",
        borderRadius: 4,
        padding: open ? "8px 10px" : "3px 7px",
        maxWidth: 320,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "block",
          color: "#e6e6e6",
        }}
        title="Which worktree/branch/origin is this?"
      >
        {import.meta.env.OXBO_WORKTREE} · {import.meta.env.OXBO_BRANCH} · :
        {import.meta.env.OXBO_PORT}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          <table style={{ borderSpacing: 0 }}>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <td style={{ paddingRight: 8, color: "#8a8a8a" }}>{k}</td>
                  <td style={{ wordBreak: "break-all" }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            onClick={() => void reset()}
            disabled={busy}
            style={{
              marginTop: 8,
              cursor: busy ? "wait" : "pointer",
              font: "inherit",
              background: "#5a1d1d",
              color: "#ffd7d7",
              border: "1px solid #7d2b2b",
              borderRadius: 3,
              padding: "3px 8px",
            }}
          >
            {busy ? "resetting…" : "Reset this origin"}
          </button>
          {note && <div style={{ marginTop: 6, color: "#ffb4b4" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}
